/**
 * FreshCards Studio Integration Hooks
 *
 * Exposes a typed API for FreshVibe Studio to control FreshCards internals.
 * Based on event bus — Studio emits events, FreshCards listens and responds.
 * This file defines the contract; real wiring is in DatabaseView + event bus.
 */
import { eventBus } from '../../../core/event-bus';
import type { DatabaseItem, PropertyDefinition, PropertyValue } from '../../../types/models/Database';
import type { PageContent } from '../../../types/models/Page';

// ─── Public Hooks Interface ────────────────────────────────────────────────

export interface FreshCardsPeekHooks {
  /** Open the Peek Panel for a specific card */
  openPeekPanel(cardId: string): void;

  /** Close the Peek Panel */
  closePeekPanel(): void;

  /** Select a card (updates Peek Chip, triggers pulse animation) */
  selectCard(cardId: string): void;

  /** Get the current Peek Chip tab state */
  getPeekChipState(): PeekChipState;

  /** Set the active tab in the Peek Chip */
  setPeekChipTab(tabId: PeekChipTabId): void;

  /** Get all properties for a card */
  getCardProperties(cardId: string): PropertyDefinition[];

  /** Update a single property on a card */
  updateCardProperty(cardId: string, propertyId: string, value: PropertyValue): void;

  /** Open the card in Center Stage (navigates away from peek) */
  openCardInCenterStage(cardId: string): void;
}

export type PeekChipTabId = 'content' | 'properties' | 'details';

export interface PeekChipState {
  cardId: string | null;
  activeTab: PeekChipTabId;
  isOpen: boolean;
  properties: PropertyChipState[];
}

export interface PropertyChipState {
  propertyId: string;
  label: string;
  value: PropertyValue;
  color?: string;
  isEditing: boolean;
}

// ─── Event Bus Channels ─────────────────────────────────────────────────────
// Studio calls these to control FreshCards peek panel

/** Open peek panel for a specific card */
export function openPeekPanel(cardId: string): void {
  eventBus.emit('fc:peek:open', { cardId });
}

/** Close peek panel */
export function closePeekPanel(): void {
  eventBus.emit('fc:peek:close', {});
}

/** Select a card in the peek panel */
export function selectCard(cardId: string): void {
  eventBus.emit('fc:peek:select', { cardId });
}

/** Set active tab */
export function setPeekChipTab(tabId: PeekChipTabId): void {
  eventBus.emit('fc:peek:setTab', { tabId });
}

/** Open card in center stage (navigate away from peek) */
export function openCardInCenterStage(cardId: string): void {
  eventBus.emit('fc:peek:openCenterStage', { cardId });
}

/** Update a card property */
export function updateCardProperty(cardId: string, propertyId: string, value: PropertyValue): void {
  eventBus.emit('fc:peek:updateProperty', { cardId, propertyId, value });
}

// ─── PeekChipState (read-only snapshot) ────────────────────────────────────
// FreshCards emits its state changes so Studio can sync its own state

export interface PeekStateSnapshot {
  cardId: string | null;
  activeTab: PeekChipTabId;
  isOpen: boolean;
}

/** Subscribe to peek state changes */
export function onPeekStateChange(handler: (snapshot: PeekStateSnapshot) => void): () => void {
  const listener = (data: PeekStateSnapshot) => handler(data);
  eventBus.on('fc:peek:stateChanged', listener);
  return () => eventBus.off('fc:peek:stateChanged', listener);
}

/** Subscribe to card selection events */
export function onCardSelected(handler: (cardId: string) => void): () => void {
  const listener = (data: { cardId: string }) => handler(data.cardId);
  eventBus.on('fc:peek:cardSelected', listener);
  return () => eventBus.off('fc:peek:cardSelected', listener);
}

/** Subscribe to property changes */
export function onPropertyChanged(
  handler: (cardId: string, propertyId: string, value: PropertyValue) => void
): () => void {
  const listener = (data: { cardId: string; propertyId: string; value: PropertyValue }) => {
    handler(data.cardId, data.propertyId, data.value);
  };
  eventBus.on('fc:peek:propertyChanged', listener);
  return () => eventBus.off('fc:peek:propertyChanged', listener);
}

/** Subscribe to peek panel close */
export function onPeekPanelClosed(handler: () => void): () => void {
  eventBus.on('fc:peek:panelClosed', handler);
  return () => eventBus.off('fc:peek:panelClosed', handler);
}

// ─── getPeekChipState ───────────────────────────────────────────────────────
// Singleton ref — updated by DatabaseView on every state change

const _peekState: PeekStateSnapshot = {
  cardId: null,
  activeTab: 'content',
  isOpen: false,
};

export function getPeekChipState(): PeekStateSnapshot {
  return { ..._peekState };
}

export function _setPeekState(snapshot: PeekStateSnapshot): void {
  _peekState.cardId = snapshot.cardId;
  _peekState.activeTab = snapshot.activeTab;
  _peekState.isOpen = snapshot.isOpen;
}

// ─── Full Hooks Object (for Studio init) ───────────────────────────────────

export const freshCardsPeekHooks: FreshCardsPeekHooks = {
  openPeekPanel,
  closePeekPanel,
  selectCard,
  getPeekChipState,
  setPeekChipTab,
  getCardProperties: (cardId) => {
    // Deferred — needs database context from DatabaseView
    eventBus.emit('fc:peek:getProperties', { cardId });
    return [];
  },
  updateCardProperty,
  openCardInCenterStage,
};

/** Convenience: expose hooks on window for Studio script injection */
export function exposePeekHooks(): void {
  if (typeof window !== 'undefined') {
    (window as any).__freshcards_peek = freshCardsPeekHooks;
    (window as any).__freshcards_peek.onCardSelected = onCardSelected;
    (window as any).__freshcards_peek.onPeekStateChange = onPeekStateChange;
    (window as any).__freshcards_peek.onPropertyChanged = onPropertyChanged;
    (window as any).__freshcards_peek.onPeekPanelClosed = onPeekPanelClosed;
  }
}